import React from 'react';

interface MoodFaceProps {
  /** Mood value from 0-100, or null for no mood */
  mood: number | null;
  /** Optional class name for the SVG container */
  className?: string;
}

const GRAY = 'rgb(200, 200, 200)';

/**
 * MoodFace component - Displays an animated SVG smiley face
 * that changes expression based on mood value (0-100)
 *
 * Ported from app/helpers/application_helper/mood_scale_presenter.rb
 */
export function MoodFace({ mood, className = '' }: MoodFaceProps) {
  const moodValue = mood ?? 50;
  const hasNoMood = mood === null;

  // Calculate face color based on mood
  const faceColor = React.useMemo(() => {
    if (hasNoMood) return GRAY;

    let red: number, green: number;
    const blue = 170;

    if (moodValue < 50) {
      // 0-50: Red to Yellow
      const intensity = moodValue / 50.0;
      red = 255;
      green = 153 + (102 * intensity);
    } else {
      // 50-100: Yellow to Green
      const intensity = (moodValue - 50) / 50.0;
      red = 255 - (102 * intensity);
      green = 255;
    }

    return `rgb(${red}, ${green}, ${blue})`;
  }, [moodValue, hasNoMood]);

  // Calculate smile positions
  const smileTopOffset = React.useMemo(() => {
    return 7 - ((moodValue - 50) / 10.0);
  }, [moodValue]);

  const smileTopPosition = React.useMemo(() => {
    if (hasNoMood || moodValue === 50) return 67;

    const distanceFromNeutral = Math.abs(moodValue - 50);
    return 60 + (distanceFromNeutral / 32.0) + smileTopOffset;
  }, [moodValue, hasNoMood, smileTopOffset]);

  const smileMiddlePosition = React.useMemo(() => {
    if (hasNoMood) return 67;

    const divider = moodValue < 50 ? 2.0 : 1.25;
    const adjustment = moodValue < 50 ? (50 - moodValue) : (moodValue - 50);
    const direction = moodValue < 50 ? -1 : 1;
    return smileTopOffset + 60 + (adjustment / divider) * direction;
  }, [moodValue, hasNoMood, smileTopOffset]);

  // Calculate upper lip path
  const upperLipD = React.useMemo(() => {
    const topOffset = moodValue < 50 ? -((50 - moodValue) / 10.0) : 0;

    if (moodValue > 75) {
      // Map 75-100 range to create open mouth smile
      const intensity = (moodValue - 75) / 25.0;
      const topCurve = smileMiddlePosition - (35 * intensity);
      return `M 35 ${smileTopPosition} Q 50 ${topCurve + topOffset} 65 ${smileTopPosition + topOffset}`;
    } else {
      return `M 35 ${smileTopPosition} Q 50 ${smileMiddlePosition} 65 ${smileTopPosition}`;
    }
  }, [moodValue, smileTopPosition, smileMiddlePosition]);

  // Calculate lower lip path
  const lowerLipD = React.useMemo(() => {
    return `M 35 ${smileTopPosition} Q 50 ${smileMiddlePosition} 65 ${smileTopPosition}`;
  }, [smileTopPosition, smileMiddlePosition]);

  const uniqueId = React.useId();

  return (
    <svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      className={`mood-scale ${className}`}
      style={{ filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))' }}
    >
      <defs>
        <radialGradient id={`faceGradient-${uniqueId}`} cx="35%" cy="35%">
          <stop offset="0%" style={{ stopColor: 'rgba(255,255,255,0.4)', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: 'rgba(255,255,255,0)', stopOpacity: 1 }} />
        </radialGradient>
      </defs>

      {/* Face circle */}
      <circle
        className="mood-scale_face"
        cx="50"
        cy="50"
        r="45"
        stroke="rgba(0,0,0,0.2)"
        strokeWidth="1.5"
        fill={faceColor}
        style={{ transition: 'fill 0.5s ease' }}
      />

      {/* Face highlight */}
      <circle
        className="mood-scale_face-highlight"
        cx="50"
        cy="50"
        r="45"
        fill={`url(#faceGradient-${uniqueId})`}
        opacity="0.6"
      />

      {/* Left eye */}
      <ellipse
        className="mood-scale_left-eye"
        cx="35"
        cy="40"
        rx="5"
        ry="6"
        fill="black"
      />

      {/* Right eye */}
      <ellipse
        className="mood-scale_right-eye"
        cx="65"
        cy="40"
        rx="5"
        ry="6"
        fill="black"
      />

      {/* Left eye reflection */}
      <ellipse
        className="mood-scale_left-eye-reflection"
        cx="37"
        cy="37"
        rx="1.5"
        ry="2"
        fill="#FFF"
        opacity="0.9"
      />

      {/* Right eye reflection */}
      <ellipse
        className="mood-scale_right-eye-reflection"
        cx="67"
        cy="37"
        rx="1.5"
        ry="2"
        fill="#FFF"
        opacity="0.9"
      />

      {/* Upper lip */}
      <path
        className="mood-scale_upper-lip"
        d={upperLipD}
        stroke="black"
        strokeWidth="3"
        strokeLinecap="round"
        fill="transparent"
        style={{ transition: 'd 0.3s ease' }}
      />

      {/* Lower lip */}
      <path
        className="mood-scale_lower-lip"
        d={lowerLipD}
        stroke="black"
        strokeWidth="3"
        strokeLinecap="round"
        fill="transparent"
        style={{ transition: 'd 0.3s ease' }}
      />
    </svg>
  );
}

/**
 * Helper function to calculate background gradient for mood display
 * Ported from mood_scale_presenter.rb background_gradient method
 */
export function getMoodBackgroundGradient(mood: number | null): string {
  if (mood === null) {
    return 'linear-gradient(135deg, rgba(200, 200, 200, 0.15), rgba(180, 180, 180, 0.08))';
  }

  const moodValue = mood;
  let color1Red: number, color1Green: number, color1Blue: number, opacity1: number;
  let color2Red: number, color2Green: number, color2Blue: number, opacity2: number;

  if (moodValue >= 50) {
    // Above neutral (50-100): gradient from yellow to bright green
    const intensity = (moodValue - 50) / 50.0;
    color1Red = 255 - 180 * intensity;
    color1Green = 255;
    color1Blue = 170 - 50 * intensity;

    color2Red = 255 - 100 * intensity;
    color2Green = 255;
    color2Blue = 220 + 35 * intensity;

    opacity1 = 0.15 + intensity * 0.20;
    opacity2 = 0.10 + intensity * 0.15;
  } else {
    // Below neutral (0-50): gradient from deep red to yellow
    const intensity = moodValue / 50.0;
    color1Red = 255;
    color1Green = 153 + 102 * intensity;
    color1Blue = 170 - 50 * (1 - intensity);

    color2Red = 255;
    color2Green = 153 + 102 * intensity;
    color2Blue = 220 - 35 * (1 - intensity);

    opacity1 = 0.15 + (1 - intensity) * 0.20;
    opacity2 = 0.10 + (1 - intensity) * 0.15;
  }

  return `linear-gradient(135deg, rgba(${color1Red}, ${color1Green}, ${color1Blue}, ${opacity1}), rgba(${color2Red}, ${color2Green}, ${color2Blue}, ${opacity2}))`;
}

/**
 * Helper function to get mood label from value
 * Ported from mood_scale_presenter.rb mood_label method
 */
export function getMoodLabel(mood: number | null, translations: Record<string, string>): string {
  if (mood === null) {
    return translations['default'] || 'How is your day?';
  }

  // Round to nearest 5 so each of the 21 grid positions on the picker
  // gets a unique label.
  const rounded = Math.round(mood / 5) * 5;

  if (translations[rounded.toString()]) {
    return translations[rounded.toString()];
  }

  // Fallback for older callers/locales: try the nearest multiple of 10.
  const rounded10 = Math.round(mood / 10) * 10;
  return translations[rounded10.toString()] || translations['50'] || 'Okay';
}
